"use client";

import { useInView } from "@/hooks/use-in-view";
import { useEffect, useRef, useState } from "react";

/** How far each character starts off-screen (px). */
export const AI_BOUNCE_PX = 72;
/** Bouncy spring — overshoots rest then settles. Drives translate only. */
export const AI_BOUNCE_SPRING_STIFFNESS = 0.22;
export const AI_BOUNCE_SPRING_DAMPING = 0.72;
/** Opacity fade duration (cubic ease-out, no spring). */
export const AI_OPACITY_EASE_MS = 280;
/** Laptop character starts shortly after the books character on enter. */
export const AI_LAPTOP_STAGGER_MS = 120;
/**
 * Bottom-only inset: the trigger is a line near the lower viewport.
 * Scroll down past it → bounce in and STAY in while the section sits at the
 * top of the screen. Scroll back up past that same line → bounce out.
 * (Do not inset the top — that wrongly untriggers when content reaches the top.)
 */
export const AI_IN_VIEW_ROOT_MARGIN = "0px 0px -35% 0px";
export const AI_IN_VIEW_THRESHOLD = 0;

/** Advance a 1D spring one frame (SlideToggle release-style). */
export function stepAiBounceSpring(
  position: number,
  velocity: number,
  target: number,
  stiffness = AI_BOUNCE_SPRING_STIFFNESS,
  damping = AI_BOUNCE_SPRING_DAMPING
): { position: number; velocity: number } {
  const nextVelocity = (velocity + (target - position) * stiffness) * damping;
  const nextPosition = position + nextVelocity;
  if (Math.abs(nextPosition - target) < 0.001 && Math.abs(nextVelocity) < 0.001) {
    return { position: target, velocity: 0 };
  }
  return { position: nextPosition, velocity: nextVelocity };
}

/** Cubic ease-out for opacity (no overshoot). */
export function easeOutCubic(t: number): number {
  const x = Math.min(1, Math.max(0, t));
  return 1 - (1 - x) ** 3;
}

/** Time-based ease-out opacity from `from` → `to`. */
export function easeOutOpacity(
  elapsedMs: number,
  from: number,
  to: number,
  durationMs = AI_OPACITY_EASE_MS
): number {
  const t = easeOutCubic(durationMs <= 0 ? 1 : elapsedMs / durationMs);
  return Number((from + (to - from) * t).toFixed(3));
}

/**
 * Horizontal bounce: progress 0 = off-screen, 1 = rest.
 * `fromLeft` true → starts negative X; false → starts positive X (from the right).
 */
export function aiBounceTranslateX(
  progress: number,
  fromLeft: boolean,
  bouncePx = AI_BOUNCE_PX
): number {
  if (progress === 1) return 0;
  const signed = fromLeft ? -bouncePx : bouncePx;
  return (1 - progress) * signed;
}

/** Books character: bounce in from the left. */
export function aiBooksTranslateX(progress: number, bouncePx = AI_BOUNCE_PX): number {
  return aiBounceTranslateX(progress, true, bouncePx);
}

/** Laptop character: bounce in from the left (same side as books, staggered). */
export function aiLaptopTranslateX(progress: number, bouncePx = AI_BOUNCE_PX): number {
  return aiBounceTranslateX(progress, true, bouncePx);
}

type SideMotion = {
  position: number;
  velocity: number;
  target: number;
  opacity: number;
  opacityFrom: number;
  opacityTo: number;
  opacityStartMs: number;
};

function settledSpring(side: SideMotion): boolean {
  return side.velocity === 0 && Math.abs(side.position - side.target) <= 0.001;
}

function settledOpacity(side: SideMotion): boolean {
  return Math.abs(side.opacity - side.opacityTo) <= 0.001;
}

export function AiSection() {
  const { ref, inView } = useInView<HTMLDivElement>({
    rootMargin: AI_IN_VIEW_ROOT_MARGIN,
    threshold: AI_IN_VIEW_THRESHOLD,
    once: false,
  });

  const [booksProgress, setBooksProgress] = useState(0);
  const [laptopProgress, setLaptopProgress] = useState(0);
  const [booksOpacity, setBooksOpacity] = useState(0);
  const [laptopOpacity, setLaptopOpacity] = useState(0);

  const booksRef = useRef<SideMotion>({
    position: 0,
    velocity: 0,
    target: 0,
    opacity: 0,
    opacityFrom: 0,
    opacityTo: 0,
    opacityStartMs: 0,
  });
  const laptopRef = useRef<SideMotion>({
    position: 0,
    velocity: 0,
    target: 0,
    opacity: 0,
    opacityFrom: 0,
    opacityTo: 0,
    opacityStartMs: 0,
  });
  const rafRef = useRef<number | null>(null);
  const staggerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reducedMotionRef = useRef(false);
  const startAnimRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    reducedMotionRef.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const tick = () => {
      const now = performance.now();

      const booksNext = stepAiBounceSpring(
        booksRef.current.position,
        booksRef.current.velocity,
        booksRef.current.target
      );
      booksRef.current.position = booksNext.position;
      booksRef.current.velocity = booksNext.velocity;
      booksRef.current.opacity = easeOutOpacity(
        now - booksRef.current.opacityStartMs,
        booksRef.current.opacityFrom,
        booksRef.current.opacityTo
      );

      const laptopNext = stepAiBounceSpring(
        laptopRef.current.position,
        laptopRef.current.velocity,
        laptopRef.current.target
      );
      laptopRef.current.position = laptopNext.position;
      laptopRef.current.velocity = laptopNext.velocity;
      laptopRef.current.opacity = easeOutOpacity(
        now - laptopRef.current.opacityStartMs,
        laptopRef.current.opacityFrom,
        laptopRef.current.opacityTo
      );

      // Publish rounded values to cut React commits on low-end devices.
      const booksP = Math.round(booksNext.position * 100) / 100;
      const laptopP = Math.round(laptopNext.position * 100) / 100;
      setBooksProgress((prev) => (prev === booksP ? prev : booksP));
      setLaptopProgress((prev) => (prev === laptopP ? prev : laptopP));
      setBooksOpacity((prev) =>
        prev === booksRef.current.opacity ? prev : booksRef.current.opacity
      );
      setLaptopOpacity((prev) =>
        prev === laptopRef.current.opacity ? prev : laptopRef.current.opacity
      );

      const done =
        settledSpring(booksRef.current) &&
        settledSpring(laptopRef.current) &&
        settledOpacity(booksRef.current) &&
        settledOpacity(laptopRef.current);

      if (!done) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        booksRef.current.position = booksRef.current.target;
        booksRef.current.velocity = 0;
        booksRef.current.opacity = booksRef.current.opacityTo;
        laptopRef.current.position = laptopRef.current.target;
        laptopRef.current.velocity = 0;
        laptopRef.current.opacity = laptopRef.current.opacityTo;
        setBooksProgress(booksRef.current.target);
        setLaptopProgress(laptopRef.current.target);
        setBooksOpacity(booksRef.current.opacityTo);
        setLaptopOpacity(laptopRef.current.opacityTo);
        rafRef.current = null;
      }
    };

    startAnimRef.current = () => {
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(tick);
    };

    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      if (staggerTimeoutRef.current != null) clearTimeout(staggerTimeoutRef.current);
      staggerTimeoutRef.current = null;
      startAnimRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (staggerTimeoutRef.current != null) {
      clearTimeout(staggerTimeoutRef.current);
      staggerTimeoutRef.current = null;
    }

    if (reducedMotionRef.current) {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      const settled = inView ? 1 : 0;
      booksRef.current = {
        position: settled,
        velocity: 0,
        target: settled,
        opacity: settled,
        opacityFrom: settled,
        opacityTo: settled,
        opacityStartMs: 0,
      };
      laptopRef.current = { ...booksRef.current };
      setBooksProgress(settled);
      setLaptopProgress(settled);
      setBooksOpacity(settled);
      setLaptopOpacity(settled);
      return;
    }

    const beginSide = (side: SideMotion, to: number) => {
      side.target = to;
      side.opacityFrom = side.opacity;
      side.opacityTo = to;
      side.opacityStartMs = performance.now();
    };

    if (inView) {
      beginSide(booksRef.current, 1);
      startAnimRef.current?.();
      staggerTimeoutRef.current = setTimeout(() => {
        beginSide(laptopRef.current, 1);
        startAnimRef.current?.();
        staggerTimeoutRef.current = null;
      }, AI_LAPTOP_STAGGER_MS);
    } else {
      beginSide(booksRef.current, 0);
      beginSide(laptopRef.current, 0);
      startAnimRef.current?.();
    }
  }, [inView]);

  const booksTranslateX = aiBooksTranslateX(booksProgress);
  const laptopTranslateX = aiLaptopTranslateX(laptopProgress);

  return (
    <section
      className="py-8 overflow-x-clip"
      data-testid="ai-section"
      data-in-view={inView ? "true" : "false"}
    >
      <div ref={ref} className="container mx-auto px-8 max-w-6xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 md:grid-rows-[1fr_auto] md:items-end">
          {/* Left block */}
          <div className="flex flex-col md:grid md:grid-rows-[1fr_auto] md:h-full">
            <div className="flex items-end justify-start mb-6 md:mb-0">
              <img
                src="/images/marketing/home/ai/balancing-books-art.webp"
                alt="Character balancing books illustration"
                data-testid="ai-books-character"
                className="w-1/2 sm:w-1/3 h-auto max-w-none select-none will-change-transform"
                style={{
                  transform: `translateX(${booksTranslateX}px)`,
                  opacity: booksOpacity,
                }}
              />
            </div>
            <div className="flex flex-col items-start text-left md:mt-6">
              <h2 className="text-white text-2xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
                Fight AI Cognitive Debt
              </h2>
              <p className="text-white/90 text-base md:text-lg leading-relaxed">
                AI is a double edged sword. While it can do a lot of our thinking for us, we need to
                be smart about how we use it. Early studies already show cognitive decline in heavy
                ChatGPT users. Keep those brains in shape with novel, fun, juicy, puzzly, mathy
                problems to solve.
              </p>
              <a
                href="https://www.media.mit.edu/publications/your-brain-on-chatgpt/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 underline text-white/90 hover:text-white"
              >
                Read MIT&apos;s study on cognitive debt & ChatGPT ➜
              </a>
            </div>
          </div>

          {/* Right block */}
          <div className="flex flex-col md:grid md:grid-rows-[1fr_auto] md:h-full">
            <div className="flex items-end justify-start mb-2 md:mb-0">
              <img
                src="/images/marketing/home/ai/using-computer-art.webp"
                alt="Character using a computer illustration"
                data-testid="ai-laptop-character"
                className="w-1/2 sm:w-1/3 h-auto max-w-none select-none will-change-transform"
                style={{
                  transform: `translateX(${laptopTranslateX}px)`,
                  opacity: laptopOpacity,
                }}
              />
            </div>
            <div className="flex flex-col items-start text-left md:mt-6">
              <h2 className="text-white text-2xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
                Prepare for the Age of AI
              </h2>
              <p className="text-white/90 text-base md:text-lg leading-relaxed">
                Beyond addressing critical skills kids need to succeed in traditional math
                education, novel creative problem solving and curiosity are more important than ever
                in the age of AI. We need the next generation to stay smarter than AI in all the
                ways they can.
              </p>
              <a
                href="https://machinelearning.apple.com/research/illusion-of-thinking"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 underline text-white/90 hover:text-white"
              >
                Read how Apple uses puzzle games to test AI ➜
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default AiSection;
